import { useState, type FormEvent } from "react";
import { aboutInfo, siteInfo } from "@/mocks/site";
import { useSeo } from "@/hooks/useSeo";
import Breadcrumb from "@/components/feature/Breadcrumb";

type SubmitStatus = "idle" | "loading" | "success" | "error";

export default function AboutPage() {
  useSeo(
    "关于本站 - 出轨同学会",
    "出轨同学会影视内容站的站点介绍、内容范围、使用说明与联系方式，帮助你了解本站定位与资料查阅方式。",
    "出轨同学会,关于本站,影视内容站,联系方式,使用说明",
  );

  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("感谢你的反馈，我们已经收到。");
      form.reset();
      return;
    }

    setStatus("loading");
    setMessage("");
    formData.delete("website_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string") {
        payload.append(key, value);
      }
    });

    try {
      const response = await fetch("https://readdy.ai/api/form/db6f4crghk82orf0ckr0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage(parsed?.meta?.message || "感谢你的反馈，我们已经收到。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "提交失败，请稍后重试。",
        );
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-8 md:py-8 lg:px-12">
      <Breadcrumb items={[{ label: "关于本站" }]} />

      <header className="mt-5 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 font-label text-xs font-medium text-accent-800">
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-flag-line text-xs leading-none"></i>
          </span>
          关于本站
        </span>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-900 md:text-3xl">
          {siteInfo.brand}影视内容站
        </h1>
        <p className="mt-3 max-w-3xl font-label text-sm leading-relaxed text-foreground-600">
          {siteInfo.intro2}
        </p>
      </header>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_360px] lg:gap-10">
        <div className="flex min-w-0 flex-col gap-6">
          {aboutInfo.sections.map((section, index) => {
            const anchorId = `about-section-${index + 1}`;
            return (
              <section
                key={section.title}
                id={anchorId}
                className="rounded-lg border border-background-200 bg-background-50 p-6"
              >
                <h4 className="font-heading text-lg font-bold text-foreground-900">
                  <a href={`#${anchorId}`} className="transition-colors hover:text-primary-700">
                    {section.title}
                  </a>
                </h4>
                <p className="mt-3 font-label text-sm leading-[1.9] text-foreground-600">
                  {section.body}
                </p>
              </section>
            );
          })}
        </div>

        <aside className="flex flex-col gap-6">
          <section className="rounded-lg border border-background-200 bg-secondary-50 p-5">
            <h4 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-900">
              <span className="flex h-4 w-4 items-center justify-center text-secondary-700">
                <i className="ri-mail-send-line text-base leading-none"></i>
              </span>
              联系方式
            </h4>
            <p className="mt-3 font-label text-sm leading-relaxed text-foreground-600">
              {siteInfo.feedbackNote}
            </p>
            <p className="mt-3 flex items-center gap-2 font-label text-sm text-foreground-800">
              <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
                <i className="ri-mail-line text-base leading-none"></i>
              </span>
              {siteInfo.email}
            </p>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h4 className="font-heading text-base font-semibold text-foreground-900">意见反馈</h4>
            <p className="mt-2 font-label text-xs leading-relaxed text-foreground-500">
              如对剧集资料有补充或纠错，欢迎在此留言，我们会尽快查看。
            </p>

            <form
              id="about-feedback-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-4 flex flex-col gap-4"
            >
              <div className="flex flex-col gap-1.5">
                <label htmlFor="fb-name" className="font-label text-xs text-foreground-600">
                  称呼
                </label>
                <input
                  id="fb-name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入你的称呼"
                  className="rounded-md border border-background-300 bg-background-100 px-3 py-2 font-label text-sm text-foreground-800 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="fb-email" className="font-label text-xs text-foreground-600">
                  邮箱
                </label>
                <input
                  id="fb-email"
                  name="email"
                  type="email"
                  required
                  placeholder="用于接收回复的邮箱"
                  className="rounded-md border border-background-300 bg-background-100 px-3 py-2 font-label text-sm text-foreground-800 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="fb-category" className="font-label text-xs text-foreground-600">
                  反馈类型
                </label>
                <select
                  id="fb-category"
                  name="category"
                  defaultValue="剧情纠错"
                  className="rounded-md border border-background-300 bg-background-100 px-3 py-2 font-label text-sm text-foreground-800 outline-none focus:border-primary-400"
                >
                  <option value="剧情纠错">剧情纠错</option>
                  <option value="演员资料">演员资料</option>
                  <option value="功能建议">功能建议</option>
                  <option value="合作联系">合作联系</option>
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="fb-message" className="font-label text-xs text-foreground-600">
                  反馈内容
                </label>
                <textarea
                  id="fb-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="请简要描述你的反馈（500字以内）"
                  className="resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2 font-label text-sm leading-relaxed text-foreground-800 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                ></textarea>
              </div>

              <input
                className="feedback-verify-field"
                name="website_alt"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 font-label text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className={status === "loading" ? "ri-loader-4-line animate-spin text-base leading-none" : "ri-send-plane-line text-base leading-none"}></i>
                </span>
                {status === "loading" ? "提交中…" : "提交反馈"}
              </button>

              {status === "success" && message && (
                <p className="flex items-start gap-2 rounded-md bg-primary-50 px-3 py-2 font-label text-xs leading-relaxed text-primary-700">
                  <span className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center">
                    <i className="ri-checkbox-circle-line text-base leading-none"></i>
                  </span>
                  {message}
                </p>
              )}

              {status === "error" && message && (
                <p className="flex items-start gap-2 rounded-md bg-accent-100 px-3 py-2 font-label text-xs leading-relaxed text-accent-800">
                  <span className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center">
                    <i className="ri-error-warning-line text-base leading-none"></i>
                  </span>
                  {message}
                </p>
              )}
            </form>
          </section>
        </aside>
      </div>
    </div>
  );
}